/* Workload alerts panel for the Director dashboard.
 * - Reads /api/teacher-workload/alerts?school_year=...
 * - Shows KPI cards (under/over/idle/without principal) + drill-down lists
 */
import { useCallback, useEffect, useState } from 'react';
import { sectionsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Warning, Clock, UserMinus, ChalkboardTeacher, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const StatCard = ({ label, value, tone = 'default', testid, hint }) => {
  const toneCls = {
    default: 'text-[#0F172A] border-[#E2E8F0]',
    warn: 'text-amber-700 border-amber-200 bg-amber-50',
    danger: 'text-red-700 border-red-200 bg-red-50',
    ok: 'text-emerald-700 border-emerald-200 bg-emerald-50',
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${toneCls}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="font-outfit text-2xl font-bold leading-tight">{value}</p>
      {hint && <p className="text-[10px] opacity-70 mt-0.5">{hint}</p>}
    </div>
  );
};

const TeacherPill = ({ row }) => (
  <li
    className="flex items-center justify-between gap-2 py-1.5 border-b border-[#F1F5F9] last:border-0 text-sm"
    data-testid={`workload-teacher-${row.teacher_id}`}
  >
    <div className="flex-1 min-w-0">
      <p className="font-medium text-[#0F172A] truncate">{row.teacher_name}</p>
      <p className="text-[10px] text-[#64748B] truncate">
        {row.classes_count} classe(s)
        {row.is_principal_of?.length ? ` · ★ ${row.is_principal_of.join(', ')}` : ''}
      </p>
    </div>
    <span className="font-outfit font-bold text-sm whitespace-nowrap">
      {row.total_hours.toFixed(1)} h
    </span>
  </li>
);

const WorkloadAlerts = ({ schoolYear }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!schoolYear) return;
    setLoading(true);
    try {
      const { data: d } = await sectionsApi.teacherWorkloadAlerts(schoolYear);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement alertes');
    } finally { setLoading(false); }
  }, [schoolYear]);

  useEffect(() => { load(); }, [load]);

  if (!data && !loading) return null;

  const counts = data?.counts || {};
  const thresholds = data?.thresholds || { under: 8, over: 25 };

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="workload-alerts-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <Warning size={18} className="text-amber-500" weight="duotone" />
          Alertes affectation — {schoolYear}
        </CardTitle>
        <Button
          variant="outline" size="sm"
          onClick={load} disabled={loading}
          data-testid="workload-alerts-refresh-btn"
        >
          <ArrowClockwise size={14} className="mr-1" /> {loading ? '…' : 'Actualiser'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Sous-charge"
            value={counts.under ?? 0}
            tone={counts.under ? 'warn' : 'ok'}
            hint={`< ${thresholds.under}h / sem`}
            testid="kpi-under"
          />
          <StatCard
            label="Surcharge"
            value={counts.over ?? 0}
            tone={counts.over ? 'danger' : 'ok'}
            hint={`> ${thresholds.over}h / sem`}
            testid="kpi-over"
          />
          <StatCard
            label="Sans créneau"
            value={counts.idle ?? 0}
            tone={counts.idle ? 'warn' : 'ok'}
            hint="0h & non principal"
            testid="kpi-idle"
          />
          <StatCard
            label="Classes sans Principal"
            value={counts.classes_without_principal ?? 0}
            tone={counts.classes_without_principal ? 'danger' : 'ok'}
            hint={`sur ${counts.classes_total ?? 0} classes`}
            testid="kpi-no-principal"
          />
        </div>

        {/* Drill-down lists */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Under-loaded */}
          <div className="border border-[#E2E8F0] rounded p-3">
            <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-2 mb-2">
              <Clock size={14} weight="duotone" /> Sous-charge
              <span className="text-xs font-normal text-[#94A3B8]">(&lt; {thresholds.under}h)</span>
            </h4>
            {(data?.under || []).length === 0 ? (
              <p className="text-xs text-emerald-600">Aucun enseignant en sous-charge.</p>
            ) : (
              <ul className="max-h-48 overflow-auto" data-testid="workload-under-list">
                {data.under.map((r) => <TeacherPill key={r.teacher_id} row={r} />)}
              </ul>
            )}
          </div>

          {/* Over-loaded */}
          <div className="border border-[#E2E8F0] rounded p-3">
            <h4 className="text-sm font-semibold text-red-700 flex items-center gap-2 mb-2">
              <Warning size={14} weight="duotone" /> Surcharge
              <span className="text-xs font-normal text-[#94A3B8]">(&gt; {thresholds.over}h)</span>
            </h4>
            {(data?.over || []).length === 0 ? (
              <p className="text-xs text-emerald-600">Aucun enseignant en surcharge.</p>
            ) : (
              <ul className="max-h-48 overflow-auto" data-testid="workload-over-list">
                {data.over.map((r) => <TeacherPill key={r.teacher_id} row={r} />)}
              </ul>
            )}
          </div>

          {/* Idle */}
          <div className="border border-[#E2E8F0] rounded p-3">
            <h4 className="text-sm font-semibold text-[#64748B] flex items-center gap-2 mb-2">
              <UserMinus size={14} weight="duotone" /> Sans créneau ni principal
            </h4>
            {(data?.idle || []).length === 0 ? (
              <p className="text-xs text-emerald-600">Tous les enseignants sont affectés.</p>
            ) : (
              <ul className="max-h-48 overflow-auto" data-testid="workload-idle-list">
                {data.idle.map((r) => <TeacherPill key={r.teacher_id} row={r} />)}
              </ul>
            )}
          </div>

          {/* Classes without principal */}
          <div className="border border-[#E2E8F0] rounded p-3">
            <h4 className="text-sm font-semibold text-red-700 flex items-center gap-2 mb-2">
              <ChalkboardTeacher size={14} weight="duotone" /> Classes sans Professeur Principal
            </h4>
            {(data?.classes_without_principal || []).length === 0 ? (
              <p className="text-xs text-emerald-600">Toutes les classes ont un Professeur Principal.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="workload-no-principal-list">
                {data.classes_without_principal.map((c) => (
                  <span
                    key={c}
                    className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium"
                    data-testid={`no-principal-${c}`}
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-[10px] text-[#94A3B8]">
          Les heures sont calculées depuis les créneaux d&apos;emploi du temps enregistrés
          pour l&apos;année <b>{data?.school_year}</b>. Ajustez les affectations depuis
          Paramètres → Sections & Enseignants et Emplois du temps.
        </p>
      </CardContent>
    </Card>
  );
};

export default WorkloadAlerts;
